import { FunctionComponent } from 'preact';

import cn from 'classnames';
import { useCallback, useEffect, useRef, useState } from 'preact/hooks';
import { CloseButton } from '../../components/buttons/CloseButton';
import { FullscreenButton } from '../../components/buttons/FullscreenButton';
import { TogglePlayButton } from '../../components/buttons/TogglePlayButton';
import { ToggleSoundButton } from '../../components/buttons/ToggleSoundButton';
import { VideoAdLink } from '../../components/VideoAdLink/VideoAdLink';
import { PauseAdLink } from '../../components/PauseAdLink/PauseAdLink';
import { usePlayer } from '../../hooks/PlayerProvider';
import { usePlayerState } from '../../hooks/usePlayerState';
import type { useUpNext } from '../../hooks/useUpNext';
import { isTapEvent } from '../../utils/events';
import { usePlayerInteraction } from '../../hooks/usePlayerInteraction';
import { DoubleTap } from '../../components/DoubleTap/DoubleTap';
import { firePixelTracker } from '../../utils/vast';
import { useTracker } from '../../hooks/useTracker';
import { useStreamType } from '../../hooks/useStreamType';
import { useTpaasTracker } from '../../hooks/useTpaasTracker';
import { ProgressBar } from '../../components/ProgressBar/ProgressBar';
import { FloatingUpNextLayout } from './FloatingUpNextLayout';
import { useCurrentAsset } from '../../hooks/CurrentAssetProvider';

export const FloatingLayout: FunctionComponent<{
  adData?: any;
  upNextState: ReturnType<typeof useUpNext>['upNextState'];
  handleUpNextCancel: () => void;
  isPauseAdVisible?: boolean;
}> = ({ adData, upNextState, handleUpNextCancel, isPauseAdVisible }) => {
  const player = usePlayer();
  const state = usePlayerState();
  const { trackEngagementUIElement } = useTpaasTracker();
  const layoutRef = useRef<HTMLDivElement>(null!);
  const { trackAdLink, trackButtonClick } = useTracker();
  const { canSeek } = useStreamType();

  const showUpNext =
    upNextState === 'preview' || upNextState === 'post-countdown';

  const togglePlay = useCallback(
    (e: TouchEvent | MouseEvent) => {
      if (isTapEvent(e)) {
        return;
      }
      if (isPauseAdVisible) {
        // Let the pause ad creative handle the click.
        return;
      }

      if (adData && state === 'playing') {
        player.pause();
        adData.clickTrackers.forEach(firePixelTracker);
        trackAdLink(adData);
        window.open(
          adData.meta.clickThroughUrl ||
            adData.meta.ima?.ad?.data?.clickThroughUrl
        );
      }

      if (state === 'playing') {
        player.pause();
      } else {
        player.play();
      }
    },
    [player, state, isPauseAdVisible]
  );

  const timeoutRef = useRef<number | null>(null);
  const [visible, setVisible] = useState(false);
  const {
    isActive,
    handleLeftDoubleTap,
    handleRightDoubleTap,
    handleProgressBarInteraction,
    handleTap,
  } = usePlayerInteraction({
    layoutRef,
    defaultIsActive: true,
  });

  const handleCloseClick = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation();

      if (isPauseAdVisible) {
        player.getPlugin('PausePlugin')?.hide();
        player.trigger('PausePlugin:pauseAdClosed');
      }

      player.setFloating(false);
      player.pause();
      trackButtonClick(
        {
          elementIdentifier: 'close_sticky_player',
          title: 'close sticky player',
          intent: 'Close',
        },
        undefined,
        undefined,
        !!adData ? 'ad' : 'asset',
        adData?.meta.adId
      );
      if (e.currentTarget instanceof Element) {
        trackEngagementUIElement({
          elementName: 'Close Floating Player',
          elementText: e.currentTarget.textContent ?? undefined,
          elementType: e.currentTarget.tagName.toLowerCase(),
          name: 'Close',
        });
      }
    },
    [isPauseAdVisible, player, trackButtonClick, trackEngagementUIElement]
  );

  const handleMouseEnter = () => {
    setVisible(true);

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = window.setTimeout(() => {
      setVisible(false);
    }, 3000);
  };

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const isVisible = visible || state === 'paused' || isActive;

  return (
    <div
      ref={layoutRef}
      className={cn('vgp-layout', {
        'paused': state === 'paused',
        'pause-ad-visible': isPauseAdVisible,
        'is-visible': isVisible,
      })}
      onClick={togglePlay}
      onMouseEnter={handleMouseEnter}
    >
      {showUpNext ? (
        <FloatingUpNextLayout
          onCancelClick={handleUpNextCancel}
          upNextState={upNextState}
        />
      ) : (
        <>
          <DoubleTap
            onTap={handleTap}
            onLeftDoubleTap={handleLeftDoubleTap}
            onRightDoubleTap={handleRightDoubleTap}
            collapsed={isVisible}
          />
          <div
            className={cn('vgp-layout-floating-icons', {
              'vgp-layout-pausedAd': isPauseAdVisible,
            })}
          >
            <FullscreenButton
              className="vgp-layout-fullscreen-button"
              color="secondary"
              size="xsmall"
            />
            <CloseButton
              onClick={handleCloseClick}
              className="vgp-layout-close-button"
              color="secondary"
              size="xsmall"
            />
            <div className="vgp-layout-pause-ad-link-wrapper">
              <TogglePlayButton
                className={cn('vgp-layout-pause-button', {
                  'vgp-layout-pause-button--adData':
                    !!adData || isPauseAdVisible,
                })}
                color="primary"
                size="xsmall"
              />

              {adData && (
                <div className="vgp-layout-pause-ad-link">
                  <VideoAdLink
                    adData={adData}
                    className="vgp-layout-pause-ad-link-content"
                    size="xsmall"
                    variant="floating"
                  />
                </div>
              )}
              {!adData && isPauseAdVisible && (
                <div className="vgp-layout-pause-ad-link">
                  <PauseAdLink size="xsmall" />
                </div>
              )}
            </div>
            <ToggleSoundButton
              className="vgp-layout-sound-button"
              color="secondary"
              size="xsmall"
            />
          </div>
          {canSeek && !isPauseAdVisible && (
            <ProgressBar
              onInteraction={handleProgressBarInteraction}
              className="vgp-floating-layout-progress-bar"
              readOnly
            />
          )}
        </>
      )}
    </div>
  );
};
